import { z } from "zod";

import { canonicalJson, sha256Hex, type JsonValue } from "./canonical.js";
import { DM2016_CALCULATION_VERSION } from "./dm2016-calculation.js";
import { DM2016_FOOTNOTE_RESOLVER_VERSION } from "./dm2016-footnotes.js";

export const DM2016_OPERATIONAL_RELEASE_GATE_VERSION = "2.0.0" as const;
export const DM2016_QUOTE_SCHEMA_VERSION = "3.0.0" as const;

export const DM2016_OPERATIONAL_REQUIRED_EVIDENCE = Object.freeze([
  "official_source_matrix",
  "independent_source_reviews",
  "institutional_examples",
  "public_calculator_regression",
  "boundary_regression",
  "authoritative_server_recalculation",
] as const);

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const artifactSchema = z.object({
  path: z.string().min(1),
  sha256: sha256Schema,
}).strict();

export const dm2016OperationalReleaseManifestSchema = z.object({
  schema_version: z.literal("1.0.0"),
  release_id: z.string().min(1),
  release_version: z.string().min(1),
  checked_as_of: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  normative_sources: z.array(z.object({
    name: z.string().min(1),
    locator: z.string().min(1),
  }).strict()).min(1),
  artifacts: z.object({
    hash_algorithm: z.literal("sha256-canonical-json"),
    z1_corpus: artifactSchema,
    z2_coverage: artifactSchema,
    z2_corpus: artifactSchema,
    selection_rules: artifactSchema,
    rounding_policy: artifactSchema,
    professionearchitetto_50_case_corpus: artifactSchema,
    core_ruleset: artifactSchema,
    attestation_public_keyring: artifactSchema,
  }).strict(),
  versions: z.object({
    calculation_engine: z.string(),
    footnote_resolver: z.string(),
    ruleset: z.string(),
    quote_schema: z.string(),
    operational_gate: z.string(),
  }).strict(),
  release_status: z.object({
    operational_calculation: z.literal("evidence_verified"),
    calculation_enabled: z.boolean(),
    legal_certification: z.literal(false),
    professional_document_requires_server_verification: z.literal(true),
    local_output_status: z.literal("facsimile_unverified"),
  }).strict(),
  default_interpretation_mode: z.literal("printed_source_strict"),
  known_divergences: z.array(z.record(z.string(), z.unknown())).min(1),
  decisions: z.object({
    qbii08: z.object({
      non_integrated_tender_base_project: z.literal("applicable"),
      non_integrated_not_tender_base_project: z.literal("not_applicable"),
      integrated_tender_base_project: z.literal("not_applicable"),
      integrated_not_tender_base_project: z.literal("not_applicable"),
    }).strict(),
    qci09: z.object({
      default_printed_q: z.literal("0.012"),
      compatibility_q: z.literal("0.12"),
      default_mode: z.literal("printed_source_strict"),
    }).strict(),
    interpolation: z.object({
      strict_interpolate: z.array(z.string()),
      strict_do_not_interpolate_implicitly: z.array(z.string()),
      compatibility_mode: z.literal("professionearchitetto_v0_74"),
    }).strict(),
    rounding: z.record(z.string(), z.unknown()),
    bim: z.record(z.string(), z.unknown()),
    expenses: z.record(z.string(), z.unknown()),
    public_economics: z.object({
      article_108_statutory_base: z.literal("cp_plus_bim_plus_expenses"),
      additional_items: z.literal("excluded_from_statutory_base_added_after_reduction"),
      direct_award_maximum_reduction_percent: z.literal("20"),
    }).strict(),
    durable_attestation: z.object({
      live_quote_algorithm: z.literal("HMAC-SHA256"),
      durable_algorithm: z.literal("Ed25519"),
      public_keyring_schema_version: z.literal("1.0.0"),
      durable_receipt_schema_version: z.literal("1.0.0"),
      historical_verification_requires_hmac_secret: z.literal(false),
      legal_certification: z.literal(false),
    }).strict(),
  }).strict(),
  evidence_counters: z.object({
    z1_destinations_verified: z.literal(61),
    z2_atomic_cells_verified: z.literal(1290),
    z2_tariff_rows_verified: z.literal(129),
    z2_services_covered: z.literal(96),
    selection_rules_verified: z.literal(58),
    professionearchitetto_compatibility_matched: z.literal(50),
    professionearchitetto_compatibility_total: z.literal(50),
    institutional_examples: z.number().int().min(1),
  }).strict(),
  test_counters: z.object({
    audit_baseline_passed: z.number().int().min(462),
    audit_baseline_failed: z.literal(0),
    professionearchitetto_matched: z.literal(50),
    professionearchitetto_total: z.literal(50),
  }).strict(),
  operational_blockers: z.array(z.string()),
  legal_certification_blockers: z.array(z.string()).min(1),
}).strict();

export type Dm2016OperationalReleaseManifest = z.infer<
  typeof dm2016OperationalReleaseManifestSchema
>;

export type Dm2016OperationalArtifactKey = Exclude<
  keyof Dm2016OperationalReleaseManifest["artifacts"],
  "hash_algorithm"
>;

export interface Dm2016OperationalReleaseArtifacts {
  readonly z1_corpus: unknown;
  readonly z2_coverage: unknown;
  readonly z2_corpus: unknown;
  readonly selection_rules: unknown;
  readonly rounding_policy: unknown;
  readonly professionearchitetto_50_case_corpus: unknown;
  readonly core_ruleset: unknown;
  readonly attestation_public_keyring: unknown;
}

export interface Dm2016OperationalReleaseIssue {
  readonly code: string;
  readonly artifact: Dm2016OperationalArtifactKey | "manifest";
  readonly message: string;
}

export interface Dm2016OperationalReleaseResult {
  readonly ready: boolean;
  readonly professionalUseAllowed: boolean;
  readonly calculationEnabled: boolean;
  readonly legalCertification: false;
  readonly manifest: Dm2016OperationalReleaseManifest | null;
  readonly issues: readonly Dm2016OperationalReleaseIssue[];
}

async function canonicalArtifactHash(value: unknown): Promise<string> {
  const cloned = JSON.parse(JSON.stringify(value)) as JsonValue;
  return sha256Hex(canonicalJson(cloned));
}

export async function evaluateDm2016OperationalRelease(input: {
  readonly manifest: unknown;
  readonly artifacts: Dm2016OperationalReleaseArtifacts;
  readonly expectedRulesetVersion: string;
}): Promise<Dm2016OperationalReleaseResult> {
  const parsed = dm2016OperationalReleaseManifestSchema.safeParse(input.manifest);
  if (!parsed.success) {
    return Object.freeze({
      ready: false,
      professionalUseAllowed: false,
      calculationEnabled: false,
      legalCertification: false,
      manifest: null,
      issues: Object.freeze([{
        code: "MANIFEST_INVALID",
        artifact: "manifest" as const,
        message: parsed.error.issues.map((issue) => issue.message).join("; "),
      }]),
    });
  }

  const manifest = parsed.data;
  const issues: Dm2016OperationalReleaseIssue[] = [];
  const versionChecks = [
    ["calculation_engine", manifest.versions.calculation_engine, DM2016_CALCULATION_VERSION],
    ["footnote_resolver", manifest.versions.footnote_resolver, DM2016_FOOTNOTE_RESOLVER_VERSION],
    ["ruleset", manifest.versions.ruleset, input.expectedRulesetVersion],
    ["quote_schema", manifest.versions.quote_schema, DM2016_QUOTE_SCHEMA_VERSION],
    ["operational_gate", manifest.versions.operational_gate, DM2016_OPERATIONAL_RELEASE_GATE_VERSION],
  ] as const;
  for (const [name, actual, expected] of versionChecks) {
    if (actual !== expected) {
      issues.push({
        code: "VERSION_MISMATCH",
        artifact: "manifest",
        message: `${name}: atteso ${expected}, trovato ${actual}`,
      });
    }
  }

  const artifactKeys = [
    "z1_corpus",
    "z2_coverage",
    "z2_corpus",
    "selection_rules",
    "rounding_policy",
    "professionearchitetto_50_case_corpus",
    "core_ruleset",
    "attestation_public_keyring",
  ] as const;
  for (const key of artifactKeys) {
    const actual = await canonicalArtifactHash(input.artifacts[key]);
    if (actual !== manifest.artifacts[key].sha256) {
      issues.push({
        code: "ARTIFACT_HASH_MISMATCH",
        artifact: key,
        message: `${manifest.artifacts[key].path}: atteso ${manifest.artifacts[key].sha256}, trovato ${actual}`,
      });
    }
  }
  if (manifest.operational_blockers.length > 0) {
    issues.push(...manifest.operational_blockers.map((blocker) => ({
      code: "OPERATIONAL_BLOCKER",
      artifact: "manifest" as const,
      message: blocker,
    })));
  }
  if (!manifest.release_status.calculation_enabled) {
    issues.push({
      code: "CALCULATION_DISABLED",
      artifact: "manifest",
      message: "Il manifest disabilita esplicitamente il calcolo operativo.",
    });
  }

  const ready = issues.length === 0;
  return Object.freeze({
    ready,
    professionalUseAllowed: ready,
    calculationEnabled: ready,
    legalCertification: false,
    manifest,
    issues: Object.freeze(issues),
  });
}
